/**
 * Gallery URLs for admin product edit.
 * Keep URL collection in sync with `extractImageUrls` in `src/utils/canonicalProductAdapter.ts`.
 * The catalog listing unions `images[]` with `source.rawData.hostedImages`; the editor
 * must do the same or extras shown in the browser cannot be managed (or removed).
 */

export type EditableProductImage = {
  url: string;
  alt: string;
  isPrimary: boolean;
};

type ProductImageLike =
  | string
  | {
      url?: string;
      alt?: string;
      isPrimary?: boolean;
    };

export type ProductWithGalleryImages = {
  images?: ProductImageLike[] | null;
  source?: {
    rawData?: {
      hostedImages?: unknown[];
    };
  } | null;
};

export function normalizeImageUrl(url: string): string {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('//') ? `https:${trimmed}` : trimmed;
}

export function isHostedImageUrl(url: string): boolean {
  const value = String(url || '').toLowerCase();
  return (
    value.includes('storage.googleapis.com/') ||
    value.includes('firebasestorage.googleapis.com/')
  );
}

export function collectManagedImages(
  product: ProductWithGalleryImages | null | undefined
): EditableProductImage[] {
  if (!product) return [];

  const metaByUrl = new Map<string, { alt: string; isPrimary: boolean }>();
  const mappedUrls: string[] = [];

  for (const image of product.images || []) {
    const url = normalizeImageUrl(typeof image === 'string' ? image : String(image?.url || ''));
    if (!url) continue;
    mappedUrls.push(url);
    const key = url.toLowerCase();
    if (typeof image !== 'string' && image) {
      const prev = metaByUrl.get(key);
      metaByUrl.set(key, {
        alt: image.alt || prev?.alt || '',
        isPrimary: Boolean(image.isPrimary) || Boolean(prev?.isPrimary),
      });
    }
  }

  const hostedFromImages = mappedUrls.filter(isHostedImageUrl);
  const rawDataHosted = (product.source?.rawData?.hostedImages || [])
    .map((url) => normalizeImageUrl(String(url || '')))
    .filter(Boolean);
  const hosted = Array.from(new Set([...hostedFromImages, ...rawDataHosted]));

  const ordered =
    hosted.length > 0
      ? Array.from(new Set([...hosted, ...mappedUrls.filter((url) => !isHostedImageUrl(url))]))
      : Array.from(new Set(mappedUrls));

  const rows = ordered.map((url) => {
    const meta = metaByUrl.get(url.toLowerCase());
    return {
      url,
      alt: meta?.alt || '',
      isPrimary: Boolean(meta?.isPrimary),
    };
  });

  if (rows.length > 0 && !rows.some((row) => row.isPrimary)) {
    rows[0].isPrimary = true;
  }
  return rows;
}

export function hostedUrlsFromImages(images: Array<{ url: string }>): string[] {
  return images.map((image) => normalizeImageUrl(image.url)).filter(isHostedImageUrl);
}

export function syncSourceHostedImages(
  source: Record<string, any> | undefined,
  imageUrls: string[]
): Record<string, any> {
  const existingRaw =
    source?.rawData && typeof source.rawData === 'object' && !Array.isArray(source.rawData)
      ? source.rawData
      : {};
  return {
    ...(source || {}),
    rawData: {
      ...existingRaw,
      hostedImages: hostedUrlsFromImages(imageUrls.map((url) => ({ url }))),
    },
  };
}
